///
/// EarthBrowser
///
/// Client-Side Air Quality Computation
///
/// Computes a US EPA style AQI from CAMS pollutant concentrations sampled
/// off the air quality grid. Model output is instantaneous rather than the
/// 8h/24h averages the official AQI formula prescribes, so treat the result
/// as an indicative AQI, not a regulatory value.
///
/// Created by Matt Giger on September 15, 2026
/// Copyright (c) 2026 Geodata Labs
///

export type AqiCategory = "good" | "moderate" | "sensitive" | "unhealthy" | "very_unhealthy" | "hazardous"

export interface PollutantConcentrations {
	pm2p5_ugm3: number | null
	pm10_ugm3: number | null
	o3_ppb: number | null
	no2_ppb: number | null
	so2_ppb: number | null
	co_ppb: number | null
}

export interface AirQualityInfo {
	aqi: number
	category: AqiCategory
	categoryLabel: string
	color: string
	dominant: keyof PollutantConcentrations
	dominantLabel: string
	pollutants: PollutantConcentrations
	time: string
}

interface Breakpoint {
	cLow: number
	cHigh: number
	iLow: number
	iHigh: number
}

// AQI index bands shared by every pollutant
const AQI_BANDS: Array<[number, number, AqiCategory, string, string]> = [
	[0, 50, "good", "Good", "#22c55e"],
	[51, 100, "moderate", "Moderate", "#facc15"],
	[101, 150, "sensitive", "Sensitive Groups", "#fb923c"],
	[151, 200, "unhealthy", "Unhealthy", "#f87171"],
	[201, 300, "very_unhealthy", "Very Unhealthy", "#c084fc"],
	[301, 500, "hazardous", "Hazardous", "#9f1239"],
]

function bands(concs: number[]): Breakpoint[] {
	return concs.slice(0, -1).map((c, i) => ({
		cLow: c,
		cHigh: concs[i + 1],
		iLow: AQI_BANDS[i][0],
		iHigh: AQI_BANDS[i][1],
	}))
}

// EPA breakpoint concentrations per pollutant (band edges, low to high).
// PM2.5 uses the 2024-updated 24h breakpoints; O3 the 8h ppb scale extended
// with the 1h scale above 100 ppb; CO values are ppm * 1000 (grid stores ppb)
const BREAKPOINTS: Record<keyof PollutantConcentrations, Breakpoint[]> = {
	pm2p5_ugm3: bands([0, 9.0, 35.4, 55.4, 125.4, 225.4, 325.4]),
	pm10_ugm3: bands([0, 54, 154, 254, 354, 424, 604]),
	o3_ppb: bands([0, 54, 70, 85, 105, 200, 604]),
	no2_ppb: bands([0, 53, 100, 360, 649, 1249, 2049]),
	so2_ppb: bands([0, 35, 75, 185, 304, 604, 1004]),
	co_ppb: bands([0, 4400, 9400, 12400, 15400, 30400, 50400]),
}

const POLLUTANT_LABELS: Record<keyof PollutantConcentrations, string> = {
	pm2p5_ugm3: "PM2.5",
	pm10_ugm3: "PM10",
	o3_ppb: "Ozone",
	no2_ppb: "NO₂",
	so2_ppb: "SO₂",
	co_ppb: "CO",
}

/** Piecewise-linear AQI sub-index for one pollutant, clamped to 500 */
function subIndex(pollutant: keyof PollutantConcentrations, concentration: number): number {
	const breakpoints = BREAKPOINTS[pollutant]
	if (concentration <= 0) return 0

	for (const bp of breakpoints) {
		if (concentration <= bp.cHigh) {
			return Math.round(bp.iLow + (bp.iHigh - bp.iLow) * (concentration - bp.cLow) / (bp.cHigh - bp.cLow))
		}
	}
	return 500
}

export function categoryForAqi(aqi: number): { category: AqiCategory, label: string, color: string } {
	for (const [low, high, category, label, color] of AQI_BANDS) {
		if (aqi >= low && aqi <= high) return { category, label, color }
	}
	const last = AQI_BANDS[AQI_BANDS.length - 1]
	return { category: last[2], label: last[3], color: last[4] }
}

/**
 * Overall AQI = max of the pollutant sub-indices; the pollutant supplying
 * the max is the dominant one. Returns null when no pollutant has data.
 */
export function computeAirQuality(pollutants: PollutantConcentrations, timeSec: number): AirQualityInfo | null {
	let aqi = -1
	let dominant: keyof PollutantConcentrations | null = null

	for (const key of Object.keys(BREAKPOINTS) as Array<keyof PollutantConcentrations>) {
		const concentration = pollutants[key]
		if (concentration === null) continue
		const index = subIndex(key, concentration)
		if (index > aqi) {
			aqi = index
			dominant = key
		}
	}

	if (dominant === null || aqi < 0) return null

	const { category, label, color } = categoryForAqi(aqi)

	return {
		aqi,
		category,
		categoryLabel: label,
		color,
		dominant,
		dominantLabel: POLLUTANT_LABELS[dominant],
		pollutants,
		time: new Date(timeSec * 1000).toISOString(),
	}
}
